import {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

// In-memory map from reservation UID to its access token
const reservationTokenMap = new Map<string, string>();

// Debug logging utility
const isDebug = process.env.NODE_ENV !== "production";
function debugLog(...args: any[]) {
  if (isDebug) {
    console.debug("[ReservationAuthInterceptor]", ...args);
  }
}

/**
 * Registers Axios interceptors on the provided apiClient to:
 * 1. Capture reservation creation responses (POST /public/mailbox/{key}),
 *    extract UID and Authorization header, and store the token.
 * 2. For any request to /reservations/{uid} or its subpaths, automatically
 *    add the stored token to the Authorization header if available.
 */
export function applyReservationAccessTokenInterceptor(
  apiClient: AxiosInstance
): void {
  // 1. Response interceptor to capture reservation creation
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      // Normalize request path by resolving against baseURL
      const requestPath =
        response.config.url && response.config.baseURL
          ? new URL(response.config.url, response.config.baseURL).pathname
          : response.config.url || "";

      try {
        const { config, data, headers } = response;
        // Match POST to /public/mailbox/{key}
        if (
          config.method === "post" &&
          requestPath &&
          /^\/public\/mailbox\/[^/]+$/.test(requestPath)
        ) {
          const uid = data?.uid as string | undefined;
          const authHeader = (headers["authorization"] ||
            headers["Authorization"]) as string | undefined;
          if (uid && authHeader) {
            const match = authHeader.match(/Bearer\s+(.+)/i);
            if (match) {
              const token = match[1];
              reservationTokenMap.set(uid, token);
              debugLog(`Stored token for reservation ${uid}`);
            } else {
              console.warn(
                `[ReservationAuthInterceptor] Malformed Authorization header for reservation ${uid}`
              );
            }
          } else {
            console.warn(
              "[ReservationAuthInterceptor] Missing UID or Authorization header in reservation creation response"
            );
          }
        }
      } catch (err) {
        console.error(
          "[ReservationAuthInterceptor] Error processing creation response:",
          err
        );
      }
      return response;
    },
    (error) => {
      // Log other response errors and propagate
      debugLog("Response error in ReservationAuthInterceptor:", error);
      return Promise.reject(error);
    }
  );

  // 2. Request interceptor to inject token for reservation endpoints
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Normalize request path by resolving against baseURL
      const requestPath =
        config.url && config.baseURL
          ? new URL(config.url, config.baseURL).pathname
          : config.url || "";

      try {
        if (requestPath) {
          // Match /reservation(s)/{uid} and any subpath
          const m = requestPath.match(/\/reservations?\/([^/]+)(?:\/.*)?$/);
          if (m) {
            const uid = m[1];
            const token = reservationTokenMap.get(uid);
            if (token) {
              config.headers["Authorization"] = `Bearer ${token}`;
              debugLog(
                `Added token for reservation ${uid} to request ${config.method?.toUpperCase()} ${
                  config.url
                }`
              );
            } else {
              console.warn(
                `[ReservationAuthInterceptor] No token found for reservation ${uid}, proceeding without Authorization header`
              );
            }
          }
        }
      } catch (err) {
        console.error(
          "[ReservationAuthInterceptor] Error processing request:",
          err
        );
      }
      return config;
    },
    (error) => {
      debugLog("Request error in ReservationAuthInterceptor:", error);
      return Promise.reject(error);
    }
  );
}
